import * as Popover from '@radix-ui/react-popover';
import { DateRange, DayPicker, DayPickerProps, Matcher } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useEffect, useState, useMemo } from 'react';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format as formatDateFns,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  sub,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { cn, convertTime12to24, getCurrentTimezone } from '@/app/lib/utils';
import { Button } from './Button';
import TimePicker from './TimePicker';
import { Calendar, ChevronDown, type LucideIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';

const formatDate = (date: Date | number, formatStr: string) => {
  return formatDateFns(date, formatStr).replace('AM', 'am').replace('PM', 'pm');
};

const formatZonedDate = (date: Date | number, timezone: string, formatStr: string) => {
  return formatInTimeZone(date, timezone, formatStr).replace('AM', 'am').replace('PM', 'pm');
};

const datePickerIconMap = {
  'calendar-fill': Calendar,
  calendar: Calendar,
  'calendar-days': Calendar,
  'arrow-down-s-fill': ChevronDown,
  'chevron-down': ChevronDown,
} satisfies Record<string, LucideIcon>;

export type DatePickerIconName = keyof typeof datePickerIconMap;

export type DatePickerProps = {
  trigger?: React.ReactNode;
  triggerClassNames?: string;
  showShortOptions?: boolean;
  selectedShortOption?: string;
  allowedShortOptions?: string[];
  applied?: Date | DateRange;
  dateFormat?: string;
  mode?: 'single' | 'range';
  onApply?: (date?: SelectedDateType<'range'> | Date) => void;
  onClear?: () => void;
  triggerDisabled?: boolean;
  iconName?: DatePickerIconName;
  icon?: LucideIcon;
  iconClassName?: string;
  iconSize?: number;
  showClearButton?: boolean;
  showTimePicker?: boolean;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  timezone?: string;
  applyOnSelect?: boolean;
  modal?: boolean;
} & Omit<DayPickerProps, 'mode'>;

const getTimezoneCalendarDate = (timezone: string, date = new Date()) => {
  try {
    const zonedDate = toZonedTime(date, timezone);
    return new Date(zonedDate.getFullYear(), zonedDate.getMonth(), zonedDate.getDate());
  } catch {
    return new Date();
  }
};

const parseDateInput = (date: string | Date) => {
  if (date instanceof Date) {
    return date;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(date);
};

export type SelectedDateType<T> = T extends 'single'
  ? Date
  : T extends 'range'
    ? DateRange
    : T extends 'multiple'
      ? Date[]
      : undefined;

const DatePicker = ({
  trigger,
  showShortOptions = false,
  selectedShortOption,
  allowedShortOptions,
  applied,
  dateFormat = 'MMM dd, yyyy',
  triggerDisabled,
  mode = 'range',
  triggerClassNames,
  iconName = 'calendar-fill',
  icon,
  iconClassName,
  iconSize = 20,
  showClearButton = true,
  showTimePicker = false,
  disableFutureDates = false,
  disablePastDates = false,
  timezone = getCurrentTimezone(),
  applyOnSelect = false,
  modal = false,
  ...props
}: DatePickerProps) => {
  const timezoneToday = useMemo(() => getTimezoneCalendarDate(timezone), [timezone]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<SelectedDateType<typeof mode> | undefined>(
    applied ||
      (mode === 'range'
        ? {
            from: startOfWeek(timezoneToday, { weekStartsOn: 0 }),
            to: timezoneToday,
          }
        : undefined)
  );

  const [selectedMonth, setSelectedMonth] = useState<Date>(
    (applied as DateRange)?.from || timezoneToday
  );
  const [selectedOption, setSelectedOption] = useState<string | null>(selectedShortOption || null);
  const [selectedTitle, setSelectedTitle] = useState<string | null>('Select Date');
  const [timeValue, setTimeValue] = useState<string>('12:00 am');
  const [rangeStart, setRangeStart] = useState<Date | undefined>(undefined);
  const [hoveredDate, setHoveredDate] = useState<Date | undefined>(undefined);

  const t = useTranslations('common.datePicker');
  const shortOptions = useMemo(() => {
    const yesterday = sub(timezoneToday, { days: 1 });
    const lastWeekReference = sub(timezoneToday, { days: 7 });
    const lastMonthReference = sub(timezoneToday, { days: 30 });

    return [
      {
        label: t('today'),
        value: 'today',
        date: [startOfDay(timezoneToday), endOfDay(timezoneToday)],
      },
      {
        label: t('yesterday'),
        value: 'yesterday',
        date: [startOfDay(yesterday), endOfDay(yesterday)],
      },
      {
        label: t('thisWeek'),
        value: 'thisWeek',
        date: [startOfWeek(timezoneToday, { weekStartsOn: 0 }), endOfDay(timezoneToday)],
      },
      {
        label: t('lastWeek'),
        value: 'lastWeek',
        date: [
          startOfWeek(lastWeekReference, { weekStartsOn: 0 }),
          endOfWeek(lastWeekReference, { weekStartsOn: 0 }),
        ],
      },
      {
        label: t('thisMonth'),
        value: 'thisMonth',
        date: [startOfMonth(timezoneToday), endOfDay(timezoneToday)],
      },
      {
        label: t('lastMonth'),
        value: 'lastMonth',
        date: [startOfMonth(lastMonthReference), endOfMonth(lastMonthReference)],
      },
      {
        label: t('thisYear'),
        value: 'thisYear',
        date: [new Date(timezoneToday.getFullYear(), 0, 1), endOfDay(timezoneToday)],
      },
    ];
  }, [timezoneToday, t]);

  const filteredShortOptions = useMemo(
    () =>
      allowedShortOptions
        ? shortOptions.filter(option => allowedShortOptions.includes(option.value))
        : shortOptions,
    [allowedShortOptions, shortOptions]
  );

  const today = useMemo(() => endOfDay(timezoneToday), [timezoneToday]);
  const startOfToday = useMemo(() => startOfDay(timezoneToday), [timezoneToday]);
  const TriggerIcon = icon || datePickerIconMap[iconName];
  const disabledDays = useMemo<DayPickerProps['disabled']>(() => {
    const disabledMatchers: Matcher[] = [];

    if (props.disabled) {
      if (Array.isArray(props.disabled)) {
        disabledMatchers.push(...props.disabled);
      } else {
        disabledMatchers.push(props.disabled);
      }
    }

    if (disableFutureDates) {
      disabledMatchers.push({ after: today });
    }

    if (disablePastDates) {
      disabledMatchers.push({ before: startOfToday });
    }

    if (disabledMatchers.length === 0) {
      return undefined;
    }

    return disabledMatchers;
  }, [disableFutureDates, disablePastDates, props.disabled, startOfToday, today]);

  useEffect(() => {
    if (mode === 'single') {
      if (applied) {
        if (typeof applied === 'string') {
          const parsedDate = parseDateInput(applied);
          setSelectedDate(parsedDate);
          setSelectedTitle(
            showTimePicker
              ? formatZonedDate(parsedDate, timezone, dateFormat)
              : formatDate(parsedDate, dateFormat)
          );
          if (showTimePicker) {
            setTimeValue(formatZonedDate(parsedDate, timezone, 'h:mm a'));
          }
        } else {
          setSelectedDate(applied as Date);
          setSelectedTitle(
            showTimePicker
              ? formatZonedDate(applied as Date, timezone, dateFormat)
              : formatDate(applied as Date, dateFormat)
          );
          if (showTimePicker) {
            setTimeValue(formatZonedDate(applied as Date, timezone, 'h:mm a'));
          }
        }
      } else {
        setSelectedDate(undefined);
        setSelectedTitle('Select Date');
        setSelectedMonth(timezoneToday);
        setTimeValue('12:00 am');
      }
    } else {
      const appliedDate = applied as DateRange;
      if (appliedDate?.from) {
        setSelectedMonth(appliedDate.from);
      } else {
        setSelectedMonth(timezoneToday);
      }

      if (appliedDate?.from && appliedDate?.to) {
        const from = formatDate(appliedDate.from as Date, dateFormat);
        const to = formatDate(appliedDate.to as Date, dateFormat);

        const isMatch = (option: (typeof filteredShortOptions)[number]) =>
          Array.isArray(option.date) &&
          formatDate(option.date[0], dateFormat) === from &&
          formatDate(option.date[1], dateFormat) === to;

        // match with the short options, prioritizing the currently selected or default option
        const shortOption =
          (selectedOption &&
            filteredShortOptions.find(
              option => option.value === selectedOption && isMatch(option)
            )) ||
          (selectedShortOption &&
            filteredShortOptions.find(
              option => option.value === selectedShortOption && isMatch(option)
            )) ||
          filteredShortOptions.find(isMatch);

        setSelectedOption(shortOption?.value || null);
        setSelectedTitle(showShortOptions && shortOption ? shortOption.label : `${from} to ${to}`);
        setSelectedDate(applied);
      }
    }
  }, [
    applied,
    dateFormat,
    filteredShortOptions,
    isOpen,
    mode,
    showShortOptions,
    showTimePicker,
    timezone,
    timezoneToday,
  ]);

  const handleTimeChange = (time: string) => {
    if (!selectedDate) {
      setTimeValue(time);
      return;
    }
    setTimeValue(time);
    // const newDate = new Date(selectedDate as Date);
    // console.log('newDate1', newDate);
    // const { hours, minutes } = convertTime12to24(time);
    // newDate.setHours(hours, minutes, 0, 0);

    // console.log('newDate', newDate);
    // setSelectedDate(newDate);
    // setSelectedTitle(formatDate(newDate, dateFormat));
    // props.onApply && props.onApply(newDate);
  };

  return (
    <div data-testid="date-picker" className="relative z-10">
      <Popover.Root
        open={isOpen}
        modal={modal}
        onOpenChange={v => {
          if (triggerDisabled) {
            setIsOpen(false);
            setRangeStart(undefined);
            setHoveredDate(undefined);
            return;
          }
          setIsOpen(v);
          if (!v) {
            setRangeStart(undefined);
            setHoveredDate(undefined);
          }
        }}
      >
        <Popover.Trigger asChild>
          {trigger || (
            <Button
              disabled={triggerDisabled}
              variant="outline"
              data-testid="date-picker-button"
              className={cn(
                'bg-white hover:bg-white dark:bg-input/30 hover:dark:bg-input/30 hober:border-input flex h-[38px] w-full justify-between items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                triggerClassNames
              )}
              color="black"
            >
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-left',
                  selectedTitle === 'Select Date' && 'text-muted-foreground font-normal'
                )}
              >
                {selectedTitle}
              </span>
              <TriggerIcon
                className={cn('text-muted-foreground shrink-0', iconClassName)}
                size={iconSize}
              />
            </Button>
          )}
        </Popover.Trigger>
        <Popover.Portal container={document.body}>
          <Popover.Content
            align="start"
            onOpenAutoFocus={event => event.preventDefault()}
            className="z-[99] rounded-md border border-border bg-popover text-popover-foreground shadow-md pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          >
            <div className="flex">
              {showShortOptions && (
                <div className="flex flex-col gap-[2px] py-2 px-2 shrink-0 w-full md:w-[107px] md:border-r border-border">
                  {filteredShortOptions.map(option => (
                    <Button
                      key={option.value}
                      color="black"
                      variant="outline"
                      className={cn(
                        'border-none whitespace-nowrap justify-center font-medium px-4 py-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground transition-all duration-200',
                        selectedOption === option.value
                          ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground'
                          : ''
                      )}
                      role="button"
                      onClick={() => {
                        setSelectedOption(option.value);
                        setSelectedDate({
                          from: option.date[0],
                          to: option.date[1],
                        });
                        setSelectedMonth(option.date[0]);
                        setRangeStart(undefined);
                        setHoveredDate(undefined);
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              )}
              <div className={cn('flex-1 flex-col', showShortOptions ? 'hidden md:flex' : 'flex')}>
                <div className="flex-1 pt-5 pb-0.5 px-2 lg:px-4">
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-expect-error */}
                  <DayPicker
                    selected={selectedDate}
                    onSelect={(range: Date | DateRange | undefined, triggerDate: Date) => {
                      if (mode === 'range') {
                        if (!rangeStart) {
                          setRangeStart(triggerDate);
                          setSelectedDate({ from: triggerDate, to: undefined });
                          setHoveredDate(undefined);
                        } else {
                          const from = triggerDate < rangeStart ? triggerDate : rangeStart;
                          const to = triggerDate < rangeStart ? rangeStart : triggerDate;
                          setSelectedDate({ from, to });
                          setRangeStart(undefined);
                          setHoveredDate(undefined);
                        }
                        return;
                      }
                      setSelectedDate(range);
                      if (applyOnSelect && mode === 'single' && range instanceof Date) {
                        props.onApply?.(range);
                        setIsOpen(false);
                      }
                    }}
                    onDayMouseEnter={(date: Date, modifiers: Record<string, boolean>) => {
                      if (rangeStart && !modifiers?.disabled) {
                        setHoveredDate(date);
                      }
                    }}
                    onDayMouseLeave={() => {
                      if (rangeStart) {
                        setHoveredDate(undefined);
                      }
                    }}
                    modifiers={{
                      range_preview: (date: Date) => {
                        if (!rangeStart || !hoveredDate) return false;
                        if (disableFutureDates && date > today) return false;
                        if (disablePastDates && date < startOfToday) return false;
                        const start = startOfDay(
                          rangeStart < hoveredDate ? rangeStart : hoveredDate
                        );
                        const end = startOfDay(rangeStart < hoveredDate ? hoveredDate : rangeStart);
                        const current = startOfDay(date);
                        return current > start && current < end;
                      },
                      range_preview_end: (date: Date) => {
                        if (!rangeStart || !hoveredDate) return false;
                        if (disableFutureDates && date > today) return false;
                        if (disablePastDates && date < startOfToday) return false;
                        return isSameDay(date, hoveredDate) && !isSameDay(date, rangeStart);
                      },
                      ...props.modifiers,
                    }}
                    modifiersClassNames={{
                      range_preview: '!bg-accent !text-accent-foreground',
                      range_preview_end: cn(
                        '!bg-accent !text-accent-foreground',
                        rangeStart && hoveredDate && hoveredDate > rangeStart
                          ? 'rounded-r-md'
                          : 'rounded-l-md'
                      ),
                      ...props.modifiersClassNames,
                    }}
                    month={selectedMonth}
                    onMonthChange={month => setSelectedMonth(month)}
                    {...props}
                    disabled={disabledDays}
                    mode={mode}
                    classNames={{
                      nav: 'absolute w-full',
                      button_previous:
                        'absolute left-1 hover:bg-accent hover:text-accent-foreground rounded-md p-1 -top-1 transition-colors duration-200',
                      button_next:
                        'absolute right-0 hover:bg-accent hover:text-accent-foreground rounded-md p-1 -top-1 transition-colors duration-200',
                      selected:
                        mode === 'single' ||
                        !(
                          selectedDate &&
                          typeof selectedDate === 'object' &&
                          'from' in selectedDate &&
                          selectedDate.from &&
                          selectedDate.to
                        )
                          ? '!bg-primary !text-primary-foreground rounded-md'
                          : undefined,
                      range_start: '!bg-primary !text-primary-foreground rounded-md',
                      range_end: '!bg-primary !text-primary-foreground rounded-md',
                      range_middle: '!bg-accent !text-accent-foreground',
                      caption_label: 'mx-auto text-foreground font-semibold text-base',
                      chevron: 'fill-muted-foreground',
                      today: 'font-semibold',
                      months: 'flex relative max-w-fit gap-2 lg:gap-4',
                      day_button:
                        'w-8 h-8 lg:w-9 lg:h-9 rounded-md transition-all duration-200 [td:not(.rdp-range_start):not(.rdp-range_end):not(.rdp-range_middle):not(.rdp-selected):not(.range_preview):not(.range_preview_end)_&]:enabled:hover:bg-accent [td:not(.rdp-range_start):not(.rdp-range_end):not(.rdp-range_middle):not(.rdp-selected):not(.range_preview):not(.range_preview_end)_&]:enabled:hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40 disabled:hover:bg-transparent [.rdp-disabled_&]:pointer-events-none [.rdp-disabled_&]:hover:bg-transparent [td.rdp-range_start_&]:hover:bg-transparent [td.rdp-range_start_&]:hover:text-primary-foreground [td.rdp-range_end_&]:hover:bg-transparent [td.rdp-range_end_&]:hover:text-primary-foreground [td.rdp-selected:not(.rdp-range_middle)_&]:hover:bg-transparent [td.rdp-selected:not(.rdp-range_middle)_&]:hover:text-primary-foreground [td.rdp-range_middle_&]:hover:bg-transparent [td.rdp-range_middle_&]:hover:!text-accent-foreground [td.range_preview_&]:hover:bg-transparent [td.range_preview_&]:hover:!text-accent-foreground [td.range_preview_end_&]:hover:bg-transparent [td.range_preview_end_&]:hover:!text-accent-foreground',
                      disabled: 'opacity-40 pointer-events-none hover:bg-transparent',
                      day: 'w-8 h-8 lg:w-9 lg:h-9',
                      ...props.classNames,
                    }}
                  />
                </div>
              </div>
            </div>
            {!(applyOnSelect && mode === 'single') && (
              <div className="flex flex-col gap-4 mb-4 mx-4 mt-0">
                {showTimePicker ? (
                  <TimePicker value={timeValue} onChange={handleTimeChange} />
                ) : null}

                <div
                  className={cn(
                    'flex justify-end gap-4',
                    showTimePicker ? 'mx-0' : 'border-t border-border pt-2.5'
                  )}
                >
                  {showClearButton && (
                    <Button
                      color="black"
                      variant="outline"
                      data-testid="date-picker-clear"
                      className="p-2 px-3 flex-1"
                      onClick={() => {
                        if (props.onClear) {
                          props.onClear();
                          setSelectedDate(undefined);
                          setSelectedTitle('Select Date');
                          setSelectedOption(null);
                          setSelectedMonth(timezoneToday);
                          setTimeValue('12:00 am');
                        } else {
                          setSelectedOption(null);
                        }
                        setRangeStart(undefined);
                        setHoveredDate(undefined);
                        setIsOpen(false);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    data-testid="date-picker-apply"
                    className={cn('p-2 px-3 flex-1', showTimePicker ? 'w-full' : 'w-fit')}
                    onClick={() => {
                      setIsOpen(false);
                      if (props.onApply) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        if (
                          typeof selectedDate === 'object' &&
                          'from' in selectedDate &&
                          'to' in selectedDate
                        ) {
                          props.onApply(selectedDate as DateRange);
                        } else {
                          const d = selectedDate as Date;
                          if (!d) {
                            return;
                          }
                          if (showTimePicker) {
                            const { hours, minutes } = convertTime12to24(timeValue);
                            const zonedDate = fromZonedTime(
                              new Date(
                                d.getFullYear(),
                                d.getMonth(),
                                d.getDate(),
                                hours,
                                minutes,
                                0,
                                0
                              ),
                              timezone
                            );
                            props.onApply(zonedDate);
                          } else {
                            props.onApply(d);
                          }
                        }
                      }
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <div id="time-picker-portal"></div>
    </div>
  );
};

export default DatePicker;
